import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PageContentContainer from "../../Container/PageContentContainer";
import PageHeadline from "../../PageHeadline/PageHeadline";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import LegalBirdPageLoading from "../../ContentLoader/LegalBirdPageLoading";
import { queryKeys } from "../../../services/ReactQuery/reactQueryService";
import {
  fetchLabelOverviewRows,
  labelOverviewProducts,
  sortLabelOverviewRows,
} from "../../../services/labelOverviewService";
import {
  LabelOverviewProductFilter,
  LabelOverviewRow,
  LabelOverviewSortColumn,
  LabelOverviewSortDirection,
} from "../../../types/LabelOverview";

type LabelOverviewTableRowsProps = {
  rows: LabelOverviewRow[];
};

const LabelOverviewTableRows = ({ rows }: LabelOverviewTableRowsProps) => {
  if (rows.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={5} align="center" sx={{ padding: "2rem" }}>
          Für die gewählte Auswahl sind keine Labels verfügbar.
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {rows.map((row) => {
        const actionIdentifierRows = row.actionIdentifiers.length > 0 ? row.actionIdentifiers : [null];

        return actionIdentifierRows.map((actionIdentifier, index) => (
          <TableRow key={`${row.product}-${row.labelCode}-${actionIdentifier?.code ?? "missing"}`}>
            {index === 0 && (
              <>
                <TableCell rowSpan={actionIdentifierRows.length} sx={{ verticalAlign: "top" }}>
                  {row.productName}
                </TableCell>
                <TableCell rowSpan={actionIdentifierRows.length} sx={{ verticalAlign: "top" }}>
                  {row.labelName}
                </TableCell>
                <TableCell rowSpan={actionIdentifierRows.length} sx={{ verticalAlign: "top" }}>
                  {row.labelCode}
                </TableCell>
              </>
            )}
            <TableCell>{actionIdentifier?.name ?? "Kein Aktionskenner konfiguriert"}</TableCell>
            <TableCell>{actionIdentifier?.code ?? "–"}</TableCell>
          </TableRow>
        ));
      })}
    </>
  );
};

const LabelOverviewPage = () => {
  const [productFilter, setProductFilter] = useState<LabelOverviewProductFilter>("all");
  const [sortColumn, setSortColumn] = useState<LabelOverviewSortColumn>("product");
  const [sortDirection, setSortDirection] = useState<LabelOverviewSortDirection>("asc");
  const {
    data: rows = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: queryKeys.all("labelOverview"),
    queryFn: fetchLabelOverviewRows,
    retry: false,
  });

  const changeSorting = (column: LabelOverviewSortColumn) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      return;
    }

    setSortColumn(column);
    setSortDirection("asc");
  };

  const filteredRows = productFilter === "all" ? rows : rows.filter(({ product }) => product === productFilter);
  const sortedRows = sortLabelOverviewRows(filteredRows, sortColumn, sortDirection);

  return (
    <PageContentContainer>
      <PageHeadline main="Label- und Kennerübersicht" />
      <Grid container sx={{ padding: "1rem" }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ValidatorSelect
            label="Produkt"
            value={productFilter}
            onChange={({ target }) => setProductFilter(target.value as LabelOverviewProductFilter)}
          >
            <MenuItem value="all">Alle Produkte</MenuItem>
            {labelOverviewProducts.map(({ key, name }) => (
              <MenuItem key={key} value={key}>
                {name}
              </MenuItem>
            ))}
          </ValidatorSelect>
        </Grid>
      </Grid>
      {isLoading && <LegalBirdPageLoading type="spinner" />}
      {error && <Alert severity="error">Die Label- und Kennerübersicht konnte nicht geladen werden.</Alert>}
      {!isLoading && !error && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: "55rem" }}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortColumn === "product" ? sortDirection : false}>
                  <TableSortLabel
                    active={sortColumn === "product"}
                    direction={sortColumn === "product" ? sortDirection : "asc"}
                    onClick={() => changeSorting("product")}
                  >
                    Produkt
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortColumn === "label" ? sortDirection : false}>
                  <TableSortLabel
                    active={sortColumn === "label"}
                    direction={sortColumn === "label" ? sortDirection : "asc"}
                    onClick={() => changeSorting("label")}
                  >
                    Label
                  </TableSortLabel>
                </TableCell>
                <TableCell>Labelkürzel</TableCell>
                <TableCell>Aktionskenner</TableCell>
                <TableCell>Aktionskennerkürzel</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <LabelOverviewTableRows rows={sortedRows} />
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PageContentContainer>
  );
};

export default LabelOverviewPage;
